import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { Router } from '@angular/router';
import { ConfirmationService } from 'primeng/api';
import { AlertMessageService } from '@sigepe/sigepe-template';
import { FeriasBaseComponent } from '../../ferias-base.component';
import { OPCOES_PERIODO_AQUISITIVO, PeriodoAquisitivo } from 'src/app/components/shared/models/periodo-aquisitivo';
import { ExercicioSelecionado, ExerciciosService, FeriasEspeciaisJudiciais } from 'src/app/services/exercicios.service';
import { formatDateToYYYYMMDD, getDateFromYYYYMMDD } from 'src/app/components/shared/date-utils';
import { Message } from 'src/app/components/shared/message';
import { Subscription } from 'rxjs';

interface FormularioFeriasEspeciaisJudiciais {
    numeroProcesso: FormControl<string>;
    sentencaJudicial: FormControl<string>;
    quantidadeDias: FormControl<number | null>;
    quantidadeMaximaParcelas: FormControl<number | null>;
    periodoAquisitivo: FormControl<string | null>;
    extraordinario: FormControl<boolean>;
    dataFimPeriodoProgramacao: FormControl<Date | null>;
    declaracaoAcaoJudicial: FormControl<boolean>;
}

type CampoFormulario = keyof FormularioFeriasEspeciaisJudiciais;
type CampoObrigatorio = Exclude<CampoFormulario, 'extraordinario'>;

const REPETICAO_EXTENSA_PROCESSO = /(\d)\1{5,}/;

declare const core: any;

interface InstanciaDsgov {
    destroy?: () => void;
}

function validarTextoNaoVazio(control: AbstractControl): ValidationErrors | null {
    const valor = control.value;
    return typeof valor === 'string' && valor.trim().length > 0 ? null : { somenteEspacos: true };
}

function validarRepeticaoExtensaProcesso(control: AbstractControl): ValidationErrors | null {
    const valor = control.value;
    if (typeof valor !== 'string' || !valor.trim()) {
        return null;
    }

    return REPETICAO_EXTENSA_PROCESSO.test(valor) ? { repeticaoExtensa: true } : null;
}

function validarPeriodoAquisitivo(control: AbstractControl): ValidationErrors | null {
    const valor = control.value;
    return valor === null || valor === 'ANC' || valor === 'PAQ' ? null : { periodoAquisitivoInvalido: true };
}

@Component({
    selector: 'app-ferias-especiais-judiciais',
    templateUrl: './ferias-especiais-judiciais.component.html',
    styleUrls: ['./ferias-especiais-judiciais.component.scss']
})
export class FeriasEspeciaisJudiciaisComponent extends FeriasBaseComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChildren('dsgovInput', { read: ElementRef })
    private readonly dsgovInputs!: QueryList<ElementRef<HTMLElement>>;
    @ViewChild('dsgovTextarea', { read: ElementRef })
    private readonly dsgovTextarea!: ElementRef<HTMLElement>;

    readonly formulario: FormGroup<FormularioFeriasEspeciaisJudiciais>;
    readonly quantidadesMaximasParcelasRegraGeral: number[] = Array.from({ length: 6 }, (_, indice) => indice + 1);
    readonly quantidadesMaximasParcelasRegraExcepcional: number[] = Array.from({ length: 90 }, (_, indice) => indice + 1);
    readonly quantidateMaximasParcelasRegraCLT: number[] = Array.from({ length: 3 }, (_, indice) => indice + 1);
    readonly opcoesPeriodoAquisitivo = OPCOES_PERIODO_AQUISITIVO;

    exercicioSelecionado?: ExercicioSelecionado;
    submetido = false;
    houvePreenchimento = false;
    dataMinimaFimPeriodoProgramacao: Date | null = null;
    calculandoPeriodoProgramacao = false;
    erroCalculoPeriodoProgramacao = false;
    private dataFimPeriodoProgramacaoCalculado: Date | null = null;
    private periodoProgramacaoSubscription?: Subscription;
    private instanciasDsgov: InstanciaDsgov[] = [];

    constructor(
        private readonly formBuilder: FormBuilder,
        private readonly exerciciosService: ExerciciosService,
        alertaMsgService: AlertMessageService,
        private readonly confirmationService: ConfirmationService,
        private readonly router: Router
    ) {
        super(alertaMsgService);
        this.formulario = this.formBuilder.group<FormularioFeriasEspeciaisJudiciais>({
            numeroProcesso: this.formBuilder.nonNullable.control('', [Validators.required, validarTextoNaoVazio, Validators.maxLength(50), validarRepeticaoExtensaProcesso]),
            sentencaJudicial: this.formBuilder.nonNullable.control('', [Validators.required, validarTextoNaoVazio, Validators.minLength(10), Validators.maxLength(300)]),
            quantidadeDias: this.formBuilder.control<number | null>(null, [Validators.required, Validators.min(1), Validators.max(90), Validators.pattern(/^\d+$/)]),
            quantidadeMaximaParcelas: this.formBuilder.control<number | null>(null, [Validators.required, control => this.validarQuantidadeMaximaParcelas(control)]),
            periodoAquisitivo: this.formBuilder.control<string | null>(null, [Validators.required, validarPeriodoAquisitivo]),
            extraordinario: this.formBuilder.nonNullable.control(false),
            dataFimPeriodoProgramacao: this.formBuilder.control<Date | null>(null),
            declaracaoAcaoJudicial: this.formBuilder.nonNullable.control(false, Validators.requiredTrue)
        });
    }

    ngOnInit(): void {
        const exercicioSelecionado = this.router.getCurrentNavigation()?.extras?.state?.['data'] ?? history.state?.data ?? JSON.parse(sessionStorage.getItem('consultaGerencialExercicioSelecionado') ?? 'null');
        this.exercicioSelecionado = exercicioSelecionado ? { ...exercicioSelecionado } : undefined;

        if (this.exercicioSelecionado) {
            this.formulario.patchValue(
                {
                    quantidadeDias: this.exercicioSelecionado.saldoDias,
                    quantidadeMaximaParcelas: this.exercicioSelecionado.quantidadeMaximaParcelas ?? null,
                    periodoAquisitivo: this.exercicioSelecionado.periodoAquisitivo === 'ANC' || this.exercicioSelecionado.periodoAquisitivo === 'PAQ' ? this.exercicioSelecionado.periodoAquisitivo : null,
                    extraordinario: this.exercicioSelecionado.extraordinario === true,
                    dataFimPeriodoProgramacao: getDateFromYYYYMMDD(this.exercicioSelecionado.dataFimPeriodoProgramacao ?? '')
                },
                { emitEvent: false }
            );
        }

        const periodoAquisitivo = this.exercicioSelecionado?.periodoAquisitivo;
        if (periodoAquisitivo === 'ANC' || periodoAquisitivo === 'PAQ') {
            this.formulario.controls.periodoAquisitivo.setValue(periodoAquisitivo, {
                emitEvent: false
            });
        }

        this.formulario.controls.extraordinario.setValue(this.exercicioSelecionado?.extraordinario === true, {
            emitEvent: false
        });
        this.dataFimPeriodoProgramacaoCalculado = getDateFromYYYYMMDD(this.exercicioSelecionado?.dataFimPeriodoProgramacao ?? '');
        this.formulario.controls.dataFimPeriodoProgramacao.setValue(this.dataFimPeriodoProgramacaoCalculado, {
            emitEvent: false
        });

        const dataInicioPeriodoProgramacao = getDateFromYYYYMMDD(this.exercicioSelecionado?.dataInicioPeriodoProgramacao ?? '');
        if (dataInicioPeriodoProgramacao) {
            this.dataMinimaFimPeriodoProgramacao = new Date(dataInicioPeriodoProgramacao);
            this.dataMinimaFimPeriodoProgramacao.setDate(this.dataMinimaFimPeriodoProgramacao.getDate() + 1);
        }

        this.atualizarValidacoesCondicionais();
        this.calcularPeriodoProgramacao(this.formulario.controls.periodoAquisitivo.value);
    }

    ngAfterViewInit(): void {
        this.instanciasDsgov = this.dsgovInputs.map(elemento => new core.BRInput('br-input', elemento.nativeElement)).concat([new core.BRTextarea('br-textarea', this.dsgovTextarea.nativeElement)]);
    }

    ngOnDestroy(): void {
        this.periodoProgramacaoSubscription?.unsubscribe();
        this.instanciasDsgov.forEach(instancia => instancia.destroy?.());
    }

    get permiteQuantidadeMaximaParcelasExcepcional(): boolean {
        const exercicio = this.exercicioSelecionado;
        return !!exercicio && (exercicio.siglaRegime === 'EST' || exercicio.siglaRegime === 'NES') && exercicio.codigoSituacaoFuncional === 7;
    }

    private get permiteQuantidadeMaximaParcelasCLT(): boolean {
        return this.exercicioSelecionado?.siglaRegime === 'CLT';
    }

    get quantidadeMaximaParcelas(): number[] {
        if (this.permiteQuantidadeMaximaParcelasExcepcional) {
            return this.quantidadesMaximasParcelasRegraExcepcional;
        }
        if (this.permiteQuantidadeMaximaParcelasCLT) {
            return this.quantidateMaximasParcelasRegraCLT;
        }
        return this.quantidadesMaximasParcelasRegraGeral;
    }

    get limiteQuantidadeMaximaParcelas(): number {
        return this.quantidadeMaximaParcelas[this.quantidadeMaximaParcelas.length - 1];
    }

    get extraordinario(): boolean {
        return this.formulario.controls.extraordinario.value;
    }

    campoInvalido(nome: CampoFormulario): boolean {
        const campo = this.formulario.controls[nome];
        return campo.invalid && (campo.touched || this.submetido);
    }

    campoObrigatorioNaoInformado(nome: CampoObrigatorio): boolean {
        if (!this.campoInvalido(nome)) {
            return false;
        }

        switch (nome) {
            case 'numeroProcesso':
                return this.formulario.controls.numeroProcesso.value.trim().length === 0;
            case 'sentencaJudicial':
                return this.formulario.controls.sentencaJudicial.value.trim().length === 0;
            case 'quantidadeDias':
                return this.formulario.controls.quantidadeDias.value === null;
            case 'quantidadeMaximaParcelas':
                return this.formulario.controls.quantidadeMaximaParcelas.value === null;
            case 'periodoAquisitivo':
                return !this.formulario.controls.periodoAquisitivo.value;
            case 'dataFimPeriodoProgramacao':
                return this.extraordinario && this.formulario.controls.dataFimPeriodoProgramacao.value === null;
            case 'declaracaoAcaoJudicial':
                return !this.formulario.controls.declaracaoAcaoJudicial.value;
        }
    }

    private get existeCampoObrigatorioNaoInformado(): boolean {
        return this.campoObrigatorioNaoInformado('numeroProcesso') || this.campoObrigatorioNaoInformado('sentencaJudicial') || this.campoObrigatorioNaoInformado('quantidadeDias') || this.campoObrigatorioNaoInformado('quantidadeMaximaParcelas') || this.campoObrigatorioNaoInformado('periodoAquisitivo') || this.campoObrigatorioNaoInformado('dataFimPeriodoProgramacao') || this.campoObrigatorioNaoInformado('declaracaoAcaoJudicial');
    }

    registrarAlteracao(): void {
        this.houvePreenchimento = true;
    }

    alterarPeriodoAquisitivo(periodoAquisitivo: string | null): void {
        this.registrarAlteracao();
        this.calcularPeriodoProgramacao(periodoAquisitivo);
    }

    private calcularPeriodoProgramacao(periodoAquisitivo: string | null): void {
        if (!this.exercicioSelecionado || (periodoAquisitivo !== 'ANC' && periodoAquisitivo !== 'PAQ')) {
            return;
        }

        const idFerias = this.exercicioSelecionado.idFerias;
        this.periodoProgramacaoSubscription?.unsubscribe();
        this.calculandoPeriodoProgramacao = true;
        this.erroCalculoPeriodoProgramacao = false;
        this.dataFimPeriodoProgramacaoCalculado = null;
        this.dataMinimaFimPeriodoProgramacao = null;
        this.exercicioSelecionado = {
            ...this.exercicioSelecionado,
            dataInicioPeriodoProgramacao: '',
            dataFimPeriodoProgramacao: ''
        };
        this.formulario.controls.dataFimPeriodoProgramacao.setValue(null, { emitEvent: false });
        this.atualizarValidacoesCondicionais();

        this.periodoProgramacaoSubscription = this.exerciciosService.obterPeriodoProgramacaoFeriasEspeciais(idFerias, periodoAquisitivo, false).subscribe({
            next: periodo => {
                const exercicioSelecionado = this.exercicioSelecionado;
                if (!exercicioSelecionado || exercicioSelecionado.idFerias !== idFerias) {
                    return;
                }

                const dataInicio = getDateFromYYYYMMDD(periodo.dataInicioPeriodoProgramacao);
                const dataFim = getDateFromYYYYMMDD(periodo.dataFimPeriodoProgramacao);
                if (!dataInicio || Number.isNaN(dataInicio.getTime()) || !dataFim || Number.isNaN(dataFim.getTime()) || dataFim.getTime() <= dataInicio.getTime()) {
                    this.falharCalculoPeriodoProgramacao();
                    return;
                }

                const dataInicioIso = formatDateToYYYYMMDD(dataInicio);
                const dataFimIso = formatDateToYYYYMMDD(dataFim);
                this.exercicioSelecionado = {
                    ...exercicioSelecionado,
                    dataInicioPeriodoProgramacao: dataInicioIso,
                    dataFimPeriodoProgramacao: dataFimIso
                };
                this.dataFimPeriodoProgramacaoCalculado = dataFim;
                this.formulario.controls.dataFimPeriodoProgramacao.setValue(dataFim, {
                    emitEvent: false
                });
                const dataMinimaFimPeriodoProgramacao = new Date(dataInicio);
                dataMinimaFimPeriodoProgramacao.setDate(dataMinimaFimPeriodoProgramacao.getDate() + 1);
                this.dataMinimaFimPeriodoProgramacao = dataMinimaFimPeriodoProgramacao;
                this.calculandoPeriodoProgramacao = false;
                this.atualizarValidacoesCondicionais();
            },
            error: () => this.falharCalculoPeriodoProgramacao()
        });
    }

    private falharCalculoPeriodoProgramacao(): void {
        this.calculandoPeriodoProgramacao = false;
        this.erroCalculoPeriodoProgramacao = true;
        this.dataFimPeriodoProgramacaoCalculado = null;
        this.dataMinimaFimPeriodoProgramacao = null;
        if (this.exercicioSelecionado) {
            this.exercicioSelecionado = {
                ...this.exercicioSelecionado,
                dataInicioPeriodoProgramacao: '',
                dataFimPeriodoProgramacao: ''
            };
        }
        this.formulario.controls.dataFimPeriodoProgramacao.setValue(null, { emitEvent: false });
        this.atualizarValidacoesCondicionais();
    }

    validarInputQuantidadeDias(event: Event): void {
        const input = event.target as HTMLInputElement;

        let valor = input.value.replace(/\D/g, '');

        if (valor.length > 2) {
            valor = valor.slice(0, 2);
        }

        if (valor === '') {
            this.formulario.get('quantidadeDias')?.reset(null, {
                emitEvent: false
            });
            return;
        }

        this.formulario.get('quantidadeDias')?.setValue(Number(valor), {
            emitEvent: false
        });

        input.value = valor;

        this.registrarAlteracao();
    }

    alterarExtraordinario(): void {
        this.registrarAlteracao();
        this.atualizarValidacoesCondicionais();
    }

    concluir(): void {
        if (this.calculandoPeriodoProgramacao || this.erroCalculoPeriodoProgramacao) {
            return;
        }

        this.submetido = true;
        this.clearMessages();
        this.atualizarValidacoesCondicionais();
        this.formulario.updateValueAndValidity({ emitEvent: false });

        if (this.formulario.invalid || !this.exercicioSelecionado) {
            this.formulario.markAllAsTouched();
            if (this.existeCampoObrigatorioNaoInformado) {
                this.getAlertaMsgService().error(Message.ERRO_CAMPOS_OBRIGATORIOS_NAO_PREENCHIDOS);
                this.rolarParaTopo();
            }
            return;
        }

        const valores = this.formulario.getRawValue();
        if (valores.quantidadeDias === null || valores.quantidadeMaximaParcelas === null || !this.ehPeriodoAquisitivo(valores.periodoAquisitivo) || (this.extraordinario && !valores.dataFimPeriodoProgramacao)) {
            this.formulario.markAllAsTouched();
            return;
        }

        const dados: FeriasEspeciaisJudiciais = {
            numeroProcesso: valores.numeroProcesso.trim(),
            sentencaJudicial: valores.sentencaJudicial.trim(),
            quantidadeDias: valores.quantidadeDias,
            quantidadeMaximaParcelas: valores.quantidadeMaximaParcelas,
            periodoAquisitivo: valores.periodoAquisitivo,
            extraordinario: valores.extraordinario,
            declaracaoAcaoJudicial: valores.declaracaoAcaoJudicial
        };

        if (this.extraordinario && valores.dataFimPeriodoProgramacao) {
            dados.dataFimPeriodoProgramacao = formatDateToYYYYMMDD(valores.dataFimPeriodoProgramacao);
        }

        this.exerciciosService.feriasEspeciaisJudiciais(this.exercicioSelecionado.idFerias, dados).subscribe({
            next: () => {
                this.getAlertaMsgService().setAwatingSuccessMessage('Férias Especiais Judiciais registradas com sucesso.');
                this.router.navigate(['/secure/gerenciar-ferias/consultar-exercicios']);
            },
            error: () => {
                this.getAlertaMsgService().setAwatingErrorMessage('Não foi possível concluir as Férias Especiais Judiciais.');
            }
        });
    }

    confirmarVoltar() {
        if (this.houvePreenchimento) {
            this.confirmationService.confirm({
                key: 'confirmPadrao',
                header: 'Confirmação',
                message: Message.CONFIRMACAO_ABANDONAR_TELA_COM_PERDA_DE_DADOS,
                accept: async () => {
                    this.voltar();
                }
            });
        } else {
            this.voltar();
        }
    }

    voltar(): void {
        this.router.navigate(['/secure/gerenciar-ferias/consultar-exercicios']);
    }

    private atualizarValidacoesCondicionais(): void {
        const dataFimPeriodoProgramacao = this.formulario.controls.dataFimPeriodoProgramacao;

        if (this.extraordinario) {
            dataFimPeriodoProgramacao.enable({ emitEvent: false });
            dataFimPeriodoProgramacao.setValidators([Validators.required, control => this.validarDataFimPeriodoProgramacao(control)]);
        } else {
            dataFimPeriodoProgramacao.setValue(this.dataFimPeriodoProgramacaoCalculado, { emitEvent: false });
            dataFimPeriodoProgramacao.clearValidators();
            dataFimPeriodoProgramacao.disable({ emitEvent: false });
        }

        dataFimPeriodoProgramacao.updateValueAndValidity({ emitEvent: false });
        this.formulario.controls.quantidadeMaximaParcelas.updateValueAndValidity({
            emitEvent: false
        });
    }

    private validarDataFimPeriodoProgramacao(control: AbstractControl): ValidationErrors | null {
        const validacaoData = this.validarDataCalendario(control);
        if (validacaoData || !control.value) {
            return validacaoData;
        }

        const dataInicioPeriodoProgramacao = getDateFromYYYYMMDD(this.exercicioSelecionado?.dataInicioPeriodoProgramacao ?? '');
        return dataInicioPeriodoProgramacao && (control.value as Date).getTime() > dataInicioPeriodoProgramacao.getTime() ? null : { dataFimPeriodoProgramacaoAnterior: true };
    }

    private validarQuantidadeMaximaParcelas(control: AbstractControl): ValidationErrors | null {
        const valor = control.value;
        if (valor === null || valor === undefined) {
            return null;
        }

        if (typeof valor !== 'number' || !Number.isInteger(valor)) {
            return { quantidadeMaximaParcelas: true };
        }

        const limite = this.permiteQuantidadeMaximaParcelasExcepcional ? 90 : 6;
        return valor >= 1 && valor <= limite ? null : { quantidadeMaximaParcelas: true };
    }

    private validarDataCalendario(control: AbstractControl): ValidationErrors | null {
        const valor = control.value;
        if (valor === null || valor === undefined) {
            return null;
        }

        return valor instanceof Date && !isNaN(valor.getTime()) ? null : { dataInvalida: true };
    }

    private ehPeriodoAquisitivo(valor: string | null): valor is PeriodoAquisitivo {
        return valor === 'ANC' || valor === 'PAQ';
    }
}
